import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Divider,
  TablePagination,
  Slide,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import AddIcon from '@mui/icons-material/Add';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import * as XLSX from 'xlsx';
import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
} from 'react-router-dom';

const GLOBAL_DOC_REF = doc(db, 'Settings', 'global');

const EMPTY_SUBJECT = {
  code: '',
  description: '',
  unit: '',
  department: '',
  course: '',
};

const DEFAULT_GLOBAL_DATA = {
  courses: [],
  departments: [],
  sections: [],
  subjects: [],
  yearLevels: [],
};

const FIELD_CONFIG = [
  {
    key: 'courses',
    label: 'Courses',
    placeholder: 'Enter course',
    icon: CategoryRoundedIcon,
    accent: '#2563eb',
    softBg: 'rgba(37, 99, 235, 0.08)',
    softBorder: 'rgba(37, 99, 235, 0.22)',
  },
  {
    key: 'departments',
    label: 'Departments',
    placeholder: 'Enter department',
    icon: ApartmentRoundedIcon,
    accent: '#7c3aed',
    softBg: 'rgba(124, 58, 237, 0.08)',
    softBorder: 'rgba(124, 58, 237, 0.22)',
  },
  {
    key: 'sections',
    label: 'Sections',
    placeholder: 'Enter section',
    icon: GroupsRoundedIcon,
    accent: '#0f766e',
    softBg: 'rgba(15, 118, 110, 0.08)',
    softBorder: 'rgba(15, 118, 110, 0.22)',
  },
  {
    key: 'subjects',
    label: 'Subjects',
    placeholder: 'Enter subject',
    icon: MenuBookRoundedIcon,
    accent: '#d97706',
    softBg: 'rgba(217, 119, 6, 0.08)',
    softBorder: 'rgba(217, 119, 6, 0.22)',
  },
  {
    key: 'yearLevels',
    label: 'Year Levels',
    placeholder: 'Enter year level',
    icon: SchoolRoundedIcon,
    accent: '#dc2626',
    softBg: 'rgba(220, 38, 38, 0.08)',
    softBorder: 'rgba(220, 38, 38, 0.22)',
  },
];

const normalizeArray = (value) => {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? '').trim());
};

const normalizeSubjects = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => ({
      code: String(item?.code ?? '').trim(),
      description: String(item?.description ?? '').trim(),
      unit: String(item?.unit ?? '').trim(),
      department: String(item?.department ?? '').trim(),
      course: String(item?.course ?? item?.program ?? '').trim(),
    }))
    .filter(
      (item) =>
        item.code ||
        item.description ||
        item.unit ||
        item.department ||
        item.course
    );
};

const sanitizeStringList = (value) => {
  return normalizeArray(value).filter(
    (item, index, arr) =>
      item && arr.findIndex((x) => x.toLowerCase() === item.toLowerCase()) === index
  );
};

const sanitizeSubjects = (value) => {
  return normalizeSubjects(value)
    .map((item) => ({
      code: item.code.trim(),
      description: item.description.trim(),
      unit: item.unit.trim(),
      department: item.department.trim(),
      course: item.course.trim(),
    }))
    .filter(
      (item, index, arr) =>
        item.code &&
        item.description &&
        item.unit &&
        item.department &&
        item.course &&
        arr.findIndex((x) => x.code.toLowerCase() === item.code.toLowerCase()) === index
    );
};

const sanitizePayload = (data) => {
  return {
    courses: sanitizeStringList(data.courses),
    departments: sanitizeStringList(data.departments),
    sections: sanitizeStringList(data.sections),
    subjects: sanitizeSubjects(data.subjects),
    yearLevels: sanitizeStringList(data.yearLevels),
  };
};

const getSubjectIdentity = (subject) => String(subject?.code ?? '').trim().toLowerCase();

const serializeSubject = (subject) =>
  [
    String(subject?.code ?? '').trim().toLowerCase(),
    String(subject?.description ?? '').trim().toLowerCase(),
    String(subject?.unit ?? '').trim().toLowerCase(),
    String(subject?.department ?? '').trim().toLowerCase(),
    String(subject?.course ?? '').trim().toLowerCase(),
  ].join('||');

const getFieldSerializedItems = (fieldKey, value) => {
  if (fieldKey === 'subjects') {
    return sanitizeSubjects(value).map(serializeSubject);
  }
  return sanitizeStringList(value).map((item) => item.toLowerCase());
};

const normalizeHeaderKey = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getRowValueByAliases = (row, aliases) => {
  const rowEntries = Object.entries(row || {});
  const matchedEntry = rowEntries.find(([key]) =>
    aliases.includes(normalizeHeaderKey(key))
  );
  return matchedEntry ? String(matchedEntry[1] ?? '').trim() : '';
};

const Preferences = () => {
  const location = useLocation();
  const navigationContext = React.useContext(NavigationContext);
  const navigator = navigationContext?.navigator;
  const subjectImportInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  const [formData, setFormData] = useState(DEFAULT_GLOBAL_DATA);
  const [originalData, setOriginalData] = useState(DEFAULT_GLOBAL_DATA);

  const [newValues, setNewValues] = useState({
    courses: '',
    departments: '',
    sections: '',
    yearLevels: '',
  });

  const [newSubject, setNewSubject] = useState(EMPTY_SUBJECT);

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [selectedFieldKey, setSelectedFieldKey] = useState('');
  const [pageByField, setPageByField] = useState({
    courses: 0,
    departments: 0,
    sections: 0,
    subjects: 0,
    yearLevels: 0,
  });

  const [openUnsavedConfirm, setOpenUnsavedConfirm] = useState(false);
  const [pendingTransition, setPendingTransition] = useState(null);

  const rowsPerPage = 7;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      height: 42,
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      fontFamily: 'Poppins, sans-serif',
      '&:hover fieldset': {
        borderColor: '#130f72',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#130f72',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Poppins, sans-serif',
      fontSize: '0.9rem',
      height: '42px',
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#94a3b8',
      opacity: 1,
    },
  };

  const importIconButtonStyles = {
    width: 42,
    height: 42,
    borderRadius: '12px',
    backgroundColor: '#130f72',
    color: '#ffc400',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947',
    },
    '&.Mui-disabled': {
      backgroundColor: '#130f72',
      color: '#ffc400',
      opacity: 0.65,
    },
  };

  const tooltipSlotProps = {
    popper: {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
      ],
    },
    tooltip: {
      sx: {
        bgcolor: '#130f72',
        color: '#ffc400',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '0.82rem',
        fontWeight: 600,
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(19, 15, 114, 0.25)',
        px: 1.25,
        py: 0.8,
      },
    },
    arrow: {
      sx: {
        color: '#130f72',
      },
    },
  };

  const fetchGlobalSettings = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      const snap = await getDoc(GLOBAL_DOC_REF);

      if (snap.exists()) {
        const data = snap.data() || {};
        const prepared = {
          courses: sanitizeStringList(data.courses),
          departments: sanitizeStringList(data.departments),
          sections: sanitizeStringList(data.sections),
          subjects: sanitizeSubjects(data.subjects),
          yearLevels: sanitizeStringList(data.yearLevels),
        };

        setFormData(prepared);
        setOriginalData(prepared);
      } else {
        await setDoc(GLOBAL_DOC_REF, DEFAULT_GLOBAL_DATA);
        setFormData(DEFAULT_GLOBAL_DATA);
        setOriginalData(DEFAULT_GLOBAL_DATA);
      }
    } catch (error) {
      console.error('Error fetching global settings:', error);
      setErrorMessage('Failed to load global settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGlobalSettings();
  }, []);

  useEffect(() => {
    if (errorMessage || successMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
        setSuccessMessage('');
      }, 5000);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [errorMessage, successMessage]);

  const hasChanges = useMemo(() => {
    return (
      JSON.stringify(sanitizePayload(formData)) !==
      JSON.stringify(sanitizePayload(originalData))
    );
  }, [formData, originalData]);

  const selectedField = useMemo(() => {
    return FIELD_CONFIG.find((field) => field.key === selectedFieldKey) || null;
  }, [selectedFieldKey]);

  const changeSummaryByField = useMemo(() => {
    const summary = {};

    FIELD_CONFIG.forEach((field) => {
      const currentItems = getFieldSerializedItems(field.key, formData[field.key]);
      const originalItems = getFieldSerializedItems(field.key, originalData[field.key]);

      const addCountRaw = currentItems.filter((item) => !originalItems.includes(item)).length;
      const removeCountRaw = originalItems.filter((item) => !currentItems.includes(item)).length;

      const pairCount = Math.min(addCountRaw, removeCountRaw);
      const addCount = addCountRaw - pairCount;
      const removeCount = removeCountRaw - pairCount;
      const editCount = pairCount;
      const totalChanges = addCount + removeCount + editCount;

      summary[field.key] = {
        totalChanges,
        hasChanges: totalChanges > 0,
      };
    });

    return summary;
  }, [formData, originalData]);

  const totalPendingChanges = useMemo(() => {
    return Object.values(changeSummaryByField).reduce(
      (acc, item) => acc + (item?.totalChanges || 0),
      0
    );
  }, [changeSummaryByField]);

  useEffect(() => {
    if (!navigator || !navigator.block) return undefined;

    const unblock = navigator.block((tx) => {
      if (!hasChanges) {
        tx.retry();
        return;
      }

      setPendingTransition(tx);
      setOpenUnsavedConfirm(true);
    });

    return unblock;
  }, [navigator, hasChanges]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasChanges) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (!hasChanges) return;

      window.history.pushState(null, '', location.pathname);
      setPendingTransition({
        retry: () => {
          window.history.back();
        },
      });
      setOpenUnsavedConfirm(true);
    };

    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasChanges, location.pathname]);

  const handleStayOnPage = () => {
    setOpenUnsavedConfirm(false);
    setPendingTransition(null);
  };

  const handleLeavePage = () => {
    setOpenUnsavedConfirm(false);

    if (pendingTransition?.retry) {
      const retry = pendingTransition.retry;
      setPendingTransition(null);
      retry();
      return;
    }

    setPendingTransition(null);
  };

  const handleItemChange = (fieldKey, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: prev[fieldKey].map((item, i) => (i === index ? value : item)),
    }));
  };

  const handleSubjectItemChange = (index, key, value) => {
    setFormData((prev) => ({
      ...prev,
      subjects: prev.subjects.map((item, i) =>
        i === index ? { ...item, [key]: value } : item
      ),
    }));
  };

  const handleNewValueChange = (fieldKey, value) => {
    setNewValues((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleNewSubjectChange = (key, value) => {
    setNewSubject((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAddItem = (fieldKey) => {
    const rawValue = newValues[fieldKey];
    const trimmedValue = rawValue.trim();

    if (!trimmedValue) return;

    const existingItems = sanitizeStringList(formData[fieldKey]);
    const alreadyExists = existingItems.some(
      (item) => item.toLowerCase() === trimmedValue.toLowerCase()
    );

    if (alreadyExists) {
      setErrorMessage(`"${trimmedValue}" already exists in ${fieldKey}.`);
      setSuccessMessage('');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldKey]: [...prev[fieldKey], trimmedValue],
    }));

    const nextLength = (formData[fieldKey]?.length || 0) + 1;
    const nextPage = Math.floor((nextLength - 1) / rowsPerPage);

    setPageByField((prev) => ({
      ...prev,
      [fieldKey]: nextPage,
    }));

    setNewValues((prev) => ({
      ...prev,
      [fieldKey]: '',
    }));

    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleAddSubject = () => {
    const payload = {
      code: newSubject.code.trim(),
      description: newSubject.description.trim(),
      unit: String(newSubject.unit).trim(),
      department: newSubject.department.trim(),
      course: newSubject.course.trim(),
    };

    if (
      !payload.code ||
      !payload.description ||
      !payload.unit ||
      !payload.department ||
      !payload.course
    ) {
      setErrorMessage('Please complete all subject fields.');
      setSuccessMessage('');
      return;
    }

    const alreadyExists = formData.subjects.some(
      (item) => item.code.trim().toLowerCase() === payload.code.toLowerCase()
    );

    if (alreadyExists) {
      setErrorMessage(`"${payload.code}" already exists in subjects.`);
      setSuccessMessage('');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      subjects: [...prev.subjects, payload],
    }));

    const nextLength = (formData.subjects?.length || 0) + 1;
    const nextPage = Math.floor((nextLength - 1) / rowsPerPage);

    setPageByField((prev) => ({
      ...prev,
      subjects: nextPage,
    }));

    setNewSubject(EMPTY_SUBJECT);
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleImportSubjectsClick = () => {
    subjectImportInputRef.current?.click();
  };

  const handleImportSubjects = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) return;

    setImporting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames?.[0];

      if (!firstSheetName) {
        throw new Error('No worksheet found in the selected file.');
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,
      });

      if (!rows.length) {
        throw new Error('The selected file is empty.');
      }

      const mappedRows = rows.map((row) => ({
        code: getRowValueByAliases(row, ['code', 'subjectcode', 'subject']),
        description: getRowValueByAliases(row, [
          'description',
          'subjectdescription',
          'descriptive',
          'descriptivetitle',
          'title',
        ]),
        unit: getRowValueByAliases(row, ['unit', 'units']),
        department: getRowValueByAliases(row, ['department', 'dept']),
        course: getRowValueByAliases(row, ['course', 'program']),
      }));

      const validRows = mappedRows.filter(
        (item) =>
          item.code &&
          item.description &&
          item.unit &&
          item.department &&
          item.course
      );

      if (!validRows.length) {
        throw new Error(
          'No valid subject rows found. Check your headers and required columns.'
        );
      }

      const existingMap = new Map(
        formData.subjects.map((item) => [item.code.trim().toLowerCase(), item])
      );

      validRows.forEach((item) => {
        existingMap.set(item.code.trim().toLowerCase(), item);
      });

      const mergedSubjects = Array.from(existingMap.values());

      setFormData((prev) => ({
        ...prev,
        subjects: mergedSubjects,
      }));

      const nextPage = Math.max(
        0,
        Math.ceil(mergedSubjects.length / rowsPerPage) - 1
      );

      setPageByField((prev) => ({
        ...prev,
        subjects: nextPage,
      }));

      const skippedCount = mappedRows.length - validRows.length;
      if (skippedCount > 0) {
        setSuccessMessage(
          `${validRows.length} subject record(s) imported successfully. ${skippedCount} invalid row(s) were skipped.`
        );
      } else {
        setSuccessMessage(`${validRows.length} subject record(s) imported successfully.`);
      }
    } catch (error) {
      console.error('Error importing subjects:', error);
      setErrorMessage(`Failed to import subjects: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  const askRemoveItem = (fieldKey, index) => {
    setDeleteTarget({ fieldKey, index });
  };

  const handleRemoveItem = () => {
    if (!deleteTarget) return;

    const { fieldKey, index } = deleteTarget;

    setFormData((prev) => {
      const updatedItems = prev[fieldKey].filter((_, i) => i !== index);

      const currentPage = pageByField[fieldKey] || 0;
      const maxPage = Math.max(0, Math.ceil(updatedItems.length / rowsPerPage) - 1);

      setPageByField((pagePrev) => ({
        ...pagePrev,
        [fieldKey]: Math.min(currentPage, maxPage),
      }));

      return {
        ...prev,
        [fieldKey]: updatedItems,
      };
    });

    setDeleteTarget(null);
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleResetChanges = () => {
    setFormData(originalData);
    setNewValues({
      courses: '',
      departments: '',
      sections: '',
      yearLevels: '',
    });
    setNewSubject(EMPTY_SUBJECT);
    setPageByField({
      courses: 0,
      departments: 0,
      sections: 0,
      subjects: 0,
      yearLevels: 0,
    });
    setErrorMessage('');
    setSuccessMessage('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');

      const payload = sanitizePayload(formData);

      await setDoc(GLOBAL_DOC_REF, payload, { merge: true });

      setFormData(payload);
      setOriginalData(payload);
      setSuccessMessage('Global settings updated successfully.');
    } catch (error) {
      console.error('Error saving global settings:', error);
      setErrorMessage('Failed to save global settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseAlert = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleChangePage = (fieldKey, newPage) => {
    setPageByField((prev) => ({
      ...prev,
      [fieldKey]: newPage,
    }));
  };

  const renderSidebarItem = (field) => {
    const IconComponent = field.icon;
    const isActive = selectedFieldKey === field.key;
    const count = formData[field.key]?.length || 0;
    const fieldChanges = changeSummaryByField[field.key] || {
      totalChanges: 0,
      hasChanges: false,
    };

    return (
      <Button
        key={field.key}
        fullWidth
        onClick={() => setSelectedFieldKey(field.key)}
        sx={{
          justifyContent: 'space-between',
          px: 2.25,
          py: 1.45,
          borderRadius: '16px',
          textTransform: 'none',
          backgroundColor: isActive ? field.softBg : '#fff',
          border: isActive ? `1px solid ${field.accent}` : '1px solid #e2e8f0',
          color: '#0f172a',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: isActive ? field.softBg : '#f8fafc',
            boxShadow: 'none',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.4, minWidth: 0, flex: 1 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              backgroundColor: field.softBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <IconComponent sx={{ color: field.accent, fontSize: 20 }} />
          </Box>

          <Box sx={{ minWidth: 0, textAlign: 'left' }}>
            <Typography
              sx={{
                color: isActive ? field.accent : '#0f172a',
                fontWeight: 700,
                fontSize: '0.98rem',
                fontFamily: 'Poppins, sans-serif',
                lineHeight: 1.2,
              }}
            >
              {field.label}
            </Typography>

            {fieldChanges.hasChanges ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  mt: 0.35,
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  sx={{
                    color: field.accent,
                    fontSize: '0.76rem',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                  }}
                >
                  {fieldChanges.totalChanges}
                </Typography>
                <Typography
                  sx={{
                    color: '#64748b',
                    fontSize: '0.76rem',
                    fontFamily: 'Poppins, sans-serif',
                  }}
                >
                  Pending changes
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.8rem',
                  fontFamily: 'Poppins, sans-serif',
                  mt: 0.2,
                }}
              >
                {count} Item/s
              </Typography>
            )}
          </Box>
        </Box>

        <ChevronRightRoundedIcon
          sx={{
            color: isActive ? field.accent : '#94a3b8',
            fontSize: 22,
            flexShrink: 0,
            ml: 7,
          }}
        />
      </Button>
    );
  };

  const renderSubjectsAddForm = () => {
    return (
      <Box sx={{ mb: 2 }}>
        <input
          ref={subjectImportInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={handleImportSubjects}
        />

        <Grid
          container
          spacing={1}
          alignItems="center"
          wrap={{ xs: 'wrap', md: 'nowrap' }}
        >
          <Grid item xs={12} sm={6} md={1.55} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={newSubject.code}
              onChange={(e) => handleNewSubjectChange('code', e.target.value)}
              placeholder="Code"
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3.05} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={newSubject.description}
              onChange={(e) => handleNewSubjectChange('description', e.target.value)}
              placeholder="Description"
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={0.9} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={newSubject.unit}
              onChange={(e) => handleNewSubjectChange('unit', e.target.value)}
              placeholder="Unit"
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={1.55} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={newSubject.department}
              onChange={(e) => handleNewSubjectChange('department', e.target.value)}
              placeholder="Department"
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={1.75} sx={{ minWidth: 0 }}>
            <TextField
              fullWidth
              size="small"
              value={newSubject.course}
              onChange={(e) => handleNewSubjectChange('course', e.target.value)}
              placeholder="Course"
              sx={inputSx}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddSubject();
                }
              }}
            />
          </Grid>

          <Grid
            item
            xs="auto"
            sm="auto"
            md="auto"
            sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}
          >
            <Tooltip title="Import Subject" arrow slotProps={tooltipSlotProps}>
              <span>
                <IconButton
                  onClick={handleImportSubjectsClick}
                  disabled={importing}
                  aria-label="Import Subject"
                  sx={importIconButtonStyles}
                >
                  {importing ? (
                    <CircularProgress size={18} sx={{ color: '#ffc400' }} />
                  ) : (
                    <FileUploadOutlinedIcon sx={{ fontSize: '1.65rem' }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>

          <Grid
            item
            xs="auto"
            sm="auto"
            md="auto"
            sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}
          >
            <Button
              variant="contained"
              color="inherit"
              onClick={handleAddSubject}
              startIcon={<AddIcon sx={{ color: '#ffc400' }} />}
              sx={{
                height: 42,
                minHeight: 42,
                minWidth: 96,
                px: 2,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                boxShadow: 'none',
                backgroundColor: '#130f72 !important',
                color: '#ffc400',
                whiteSpace: 'nowrap',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0f0c5c !important',
                  boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
                },
                '&:active': {
                  backgroundColor: '#0b0947 !important',
                },
              }}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderSubjectRows = (field, paginatedItems, currentPage) => {
    const originalLookup = new Set(
      sanitizeSubjects(originalData.subjects).map((item) => getSubjectIdentity(item))
    );

    return paginatedItems.map((item, localIndex) => {
      const actualIndex = currentPage * rowsPerPage + localIndex;
      const isNewItem = !originalLookup.has(getSubjectIdentity(item));

      return (
        <Box
          key={`subjects-${actualIndex}`}
          sx={{
            borderRadius: '16px',
            border: isNewItem ? `1px solid ${field.softBorder}` : '1px solid #e2e8f0',
            backgroundColor: isNewItem ? field.softBg : '#ffffff',
            p: 1.2,
          }}
        >
          <Grid container spacing={1.1} alignItems="center">
            <Grid item xs={12} sm={6} md={1.9}>
              <TextField
                fullWidth
                size="small"
                value={item.code}
                onChange={(e) => handleSubjectItemChange(actualIndex, 'code', e.target.value)}
                placeholder="Code"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3.2}>
              <TextField
                fullWidth
                size="small"
                value={item.description}
                onChange={(e) =>
                  handleSubjectItemChange(actualIndex, 'description', e.target.value)
                }
                placeholder="Description"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1.0}>
              <TextField
                fullWidth
                size="small"
                value={item.unit}
                onChange={(e) => handleSubjectItemChange(actualIndex, 'unit', e.target.value)}
                placeholder="Unit"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                value={item.department}
                onChange={(e) =>
                  handleSubjectItemChange(actualIndex, 'department', e.target.value)
                }
                placeholder="Department"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} sm={4} md={1.5}>
              <TextField
                fullWidth
                size="small"
                value={item.course}
                onChange={(e) =>
                  handleSubjectItemChange(actualIndex, 'course', e.target.value)
                }
                placeholder="Course"
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} md={0.7}>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-end', md: 'center' } }}>
                <IconButton
                  onClick={() => askRemoveItem('subjects', actualIndex)}
                  sx={{
                    color: '#dc2626',
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '12px',
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: '#fee2e2',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    });
  };

  const renderSimpleRows = (field, paginatedItems, currentPage) => {
    const originalLookup = new Set(
      sanitizeStringList(originalData[field.key]).map((item) => item.toLowerCase())
    );

    return paginatedItems.map((item, localIndex) => {
      const actualIndex = currentPage * rowsPerPage + localIndex;
      const isNewItem = !originalLookup.has(String(item).trim().toLowerCase());

      return (
        <Box
          key={`${field.key}-${actualIndex}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <TextField
            fullWidth
            size="small"
            value={item}
            onChange={(e) => handleItemChange(field.key, actualIndex, e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 42,
                borderRadius: '12px',
                backgroundColor: isNewItem ? field.softBg : '#ffffff',
                fontFamily: 'Poppins, sans-serif',
                border: isNewItem ? `1px solid ${field.softBorder}` : 'none',
                '&:hover fieldset': {
                  borderColor: isNewItem ? field.accent : '#130f72',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isNewItem ? field.accent : '#130f72',
                },
              },
              '& .MuiInputBase-input': {
                color: isNewItem ? field.accent : '#0f172a',
                fontWeight: isNewItem ? 700 : 400,
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.9rem',
                height: '42px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />

          <IconButton
            onClick={() => askRemoveItem(field.key, actualIndex)}
            sx={{
              color: '#dc2626',
              backgroundColor: '#fff5f5',
              border: '1px solid #fed7d7',
              borderRadius: '12px',
              width: 42,
              height: 42,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: '#fee2e2',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    });
  };

  const renderEditor = (field) => {
    if (!field) return null;

    const FieldIcon = field.icon;
    const allItems = formData[field.key] || [];
    const currentPage = pageByField[field.key] || 0;
    const paginatedItems = allItems.slice(
      currentPage * rowsPerPage,
      currentPage * rowsPerPage + rowsPerPage
    );

    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          p: 2.5,
          borderRadius: '20px',
          background:
            'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{
                color: '#130f72',
                fontWeight: 800,
                fontSize: '1.1rem',
                fontFamily: 'Space Grotesk, sans-serif',
                lineHeight: 1.2,
              }}
            >
              {field.label}
            </Typography>
            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.82rem',
                fontFamily: 'Poppins, sans-serif',
                mt: 0.35,
              }}
            >
              Manage available {field.label.toLowerCase()}
            </Typography>
          </Box>

          <Box
            sx={{
              minWidth: 120,
              height: 36,
              px: 1.5,
              borderRadius: '999px',
              backgroundColor: 'rgba(19, 15, 114, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                color: '#130f72',
                fontWeight: 800,
                fontSize: '0.88rem',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {allItems.length || 0} Item/s
            </Typography>
          </Box>
        </Box>

        {field.key === 'subjects' ? (
          renderSubjectsAddForm()
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mb: 2,
              alignItems: 'stretch',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <TextField
              fullWidth
              size="small"
              value={newValues[field.key] ?? ''}
              onChange={(e) => handleNewValueChange(field.key, e.target.value)}
              placeholder={field.placeholder}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddItem(field.key);
                }
              }}
              sx={inputSx}
            />

            <Button
              variant="contained"
              color="inherit"
              onClick={() => handleAddItem(field.key)}
              startIcon={<AddIcon sx={{ color: '#ffc400' }} />}
              sx={{
                minWidth: { xs: '100%', sm: 110 },
                height: 42,
                minHeight: 42,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                boxShadow: 'none',
                backgroundColor: '#130f72 !important',
                color: '#ffc400',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0f0c5c !important',
                  boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
                },
                '&:active': {
                  backgroundColor: '#0b0947 !important',
                },
              }}
            >
              Add
            </Button>
          </Box>
        )}

        <Divider sx={{ mb: 2, borderColor: '#edf2f7' }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.15 }}>
          {paginatedItems.length > 0 ? (
            field.key === 'subjects'
              ? renderSubjectRows(field, paginatedItems, currentPage)
              : renderSimpleRows(field, paginatedItems, currentPage)
          ) : (
            <Box
              sx={{
                py: 5,
                px: 3,
                textAlign: 'center',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 74,
                  height: 74,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <FieldIcon sx={{ fontSize: 34, color: field.accent }} />
              </Box>

              <Typography
                sx={{
                  color: '#0f172a',
                  fontWeight: 800,
                  fontSize: '1rem',
                  fontFamily: 'Space Grotesk, sans-serif',
                  lineHeight: 1.2,
                  mb: 0.7,
                }}
              >
                No {field.label.toLowerCase()} yet
              </Typography>

              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.9rem',
                  fontFamily: 'Poppins, sans-serif',
                  lineHeight: 1.7,
                  maxWidth: 420,
                }}
              >
                There are no {field.label.toLowerCase()} in the database yet. Add your first item
                above to get started.
              </Typography>
            </Box>
          )}
        </Box>

        {allItems.length > rowsPerPage && (
          <TablePagination
            rowsPerPageOptions={[7]}
            component="div"
            count={allItems.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={(event, newPage) => handleChangePage(field.key, newPage)}
            sx={{
              mt: 1.5,
              px: 0,
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontFamily: 'Poppins, sans-serif',
                color: 'grey.400',
              },
              '& .MuiTablePagination-actions .MuiIconButton-root': {
                color: '#130f72',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: '#0f0c5c',
                  backgroundColor: 'rgba(19, 15, 114, 0.05)',
                },
                '&.Mui-disabled': {
                  color: 'grey.400',
                },
              },
              '& .MuiSvgIcon-root': {
                color: 'inherit',
              },
            }}
          />
        )}
      </Paper>
    );
  };

  const renderEmptyState = () => {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: 420,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 5,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 460,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              width: 84,
              height: 84,
              borderRadius: '22px',
              background: 'rgba(19, 15, 114, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2.2,
            }}
          >
            <TuneRoundedIcon sx={{ fontSize: 40, color: '#130f72' }} />
          </Box>

          <Typography
            sx={{
              color: '#130f72',
              fontWeight: 800,
              fontSize: '1.4rem',
              fontFamily: 'Space Grotesk, sans-serif',
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            No category selected
          </Typography>

          <Typography
            sx={{
              color: '#64748b',
              fontSize: '0.98rem',
              fontFamily: 'Poppins, sans-serif',
              lineHeight: 1.8,
            }}
          >
            Select a preference category from the left to view and edit its items.
          </Typography>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#130f72', mb: 2 }} />
          <Typography sx={{ color: '#130f72', fontWeight: 700 }}>
            Loading preferences...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'fixed',
          top: 45,
          right: 55,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: 'fit-content',
          maxWidth: 'none',
        }}
      >
        {errorMessage && (
          <Alert
            severity="error"
            variant="filled"
            onClose={handleCloseAlert}
            sx={{
              minWidth: 320,
              maxWidth: 520,
              borderRadius: '12px',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
              bgcolor: '#b90d0d',
              color: '#fff',
              whiteSpace: 'nowrap',
              alignItems: 'center',
              '& .MuiAlert-icon': {
                color: '#fff',
                alignSelf: 'center',
              },
              '& .MuiAlert-message': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
              },
            }}
          >
            {errorMessage}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            variant="filled"
            onClose={handleCloseAlert}
            sx={{
              minWidth: 320,
              maxWidth: 520,
              borderRadius: '12px',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
              bgcolor: '#0f56b3',
              color: '#fff',
              whiteSpace: 'nowrap',
              alignItems: 'center',
              '& .MuiAlert-icon': {
                color: '#fff',
                alignSelf: 'center',
              },
              '& .MuiAlert-message': {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
              '& .MuiAlert-action': {
                alignItems: 'center',
              },
            }}
          >
            {successMessage}
          </Alert>
        )}
      </Box>

      <Slide
        in={hasChanges}
        direction="down"
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 450, exit: 250 }}
      >
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            top: 30,
            right: 40,
            zIndex: 1200,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: 'calc(100vw - 32px)',
            px: 2,
            py: 1.8,
            borderRadius: '20px',
            border: '1px solid #e2e8f0',
            background: '#ffffff',
            boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
            boxSizing: 'border-box',
            pointerEvents:
              hasChanges && !deleteTarget && !openUnsavedConfirm ? 'auto' : 'none',
            opacity: deleteTarget || openUnsavedConfirm ? 0.55 : 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.25,
              mb: 1.4,
            }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '12px',
                backgroundColor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                border: '1px solid #e2e8f0',
              }}
            >
              <WarningAmberRoundedIcon sx={{ color: '#64748b', fontSize: 22 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#0f172a',
                  fontWeight: 800,
                  fontSize: '0.98rem',
                  fontFamily: 'Space Grotesk, sans-serif',
                  lineHeight: 1.2,
                }}
              >
                Unsaved changes
              </Typography>
              <Typography
                sx={{
                  color: '#64748b',
                  fontSize: '0.82rem',
                  fontFamily: 'Poppins, sans-serif',
                  mt: 0.35,
                  lineHeight: 1.5,
                }}
              >
                You have {totalPendingChanges} pending change{totalPendingChanges !== 1 ? 's' : ''}.
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={<RestartAltRoundedIcon />}
              onClick={handleResetChanges}
              disabled={saving || !!deleteTarget || openUnsavedConfirm}
              sx={{
                flex: 1,
                height: 42,
                minHeight: 42,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                borderColor: '#d0d7e2',
                color: '#334155',
                backgroundColor: '#ffffff',
                '&:hover': {
                  borderColor: '#130f72',
                  backgroundColor: 'rgba(19, 15, 114, 0.04)',
                },
              }}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              color="inherit"
              startIcon={
                saving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SaveRoundedIcon sx={{ color: '#ffc400' }} />
                )
              }
              onClick={handleSave}
              disabled={saving || !!deleteTarget || openUnsavedConfirm}
              sx={{
                flex: 1.1,
                height: 42,
                minHeight: 42,
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Poppins, sans-serif',
                boxShadow: 'none',
                backgroundColor: '#130f72 !important',
                color: '#ffc400',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0f0c5c !important',
                  boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
                },
                '&:active': {
                  backgroundColor: '#0b0947 !important',
                },
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Paper>
      </Slide>

      <Box
        sx={{
          mx: 5,
          mt: '33px',
          mb: 5,
          width: 'auto',
        }}
      >
        <Grid container spacing={3} alignItems="flex-start" wrap="nowrap">
          <Grid item xs={12} md={4.4} lg={4}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  px: 2.25,
                  py: 2,
                  borderRadius: '22px',
                  background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  position: 'sticky',
                  top: 24,
                  boxSizing: 'border-box',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                  {FIELD_CONFIG.map((field) => renderSidebarItem(field))}
                </Box>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs zeroMinWidth sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ width: '100%' }}>
              {selectedField ? renderEditor(selectedField) : renderEmptyState()}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 700,
            color: '#130f72',
          }}
        >
          Remove Item
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: 'Poppins, sans-serif',
              marginX: 5,
            }}
          >
            Are you sure you want to remove this item?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#130f72',
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleRemoveItem}
            autoFocus
            color="error"
            sx={{
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openUnsavedConfirm} onClose={handleStayOnPage}>
        <DialogTitle
          sx={{
            fontFamily: '"Space Grotesk", sans-serif',
            fontWeight: 700,
            color: '#130f72',
          }}
        >
          Unsaved Changes
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            sx={{
              fontFamily: 'Poppins, sans-serif',
              marginX: 5,
            }}
          >
            You have unsaved changes. Are you sure you want to leave this page?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleStayOnPage}
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: '#130f72',
            }}
          >
            Stay
          </Button>

          <Button
            onClick={handleLeavePage}
            autoFocus
            color="error"
            sx={{
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            Leave
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Preferences;